/**
 * Where an asset leads, as data. Pure and universal: NOVA Home, the asset
 * pages and the weekly digest email each turn it into their own link.
 *
 * Aliases: asset destination, asset link target
 */

import type { CoachAssetView } from "~/lib/functions/nova-assets";
import type {
  AssetKey,
  ContentAssetKey,
} from "~/lib/shared/nova/assets/catalog";
import {
  assetByKey,
  hasAssetPage,
  isContentAssetKey,
} from "~/lib/shared/nova/assets/catalog";

/**
 * Where an asset's target leads when it is a place rather than an action: the
 * asset's own page, My content under the asset's type for a content
 * collection, the page the catalog names (the score), or billing. Null for a
 * start with no page behind it, and for a card with nowhere to go.
 *
 * `explain_trial` leads to billing too — the card only puts an explainer in
 * front of the same link. Every surface that turns a target into a link reads
 * it here, so the card and the asset page cannot send a coach to different
 * places.
 */
export type AssetDestination =
  | { readonly kind: "asset_page"; readonly asset: AssetKey }
  | { readonly kind: "content"; readonly type: ContentAssetKey }
  | { readonly kind: "page"; readonly route: string }
  // The link itself carries the screen it sits on, so checkout returns there.
  | { readonly kind: "billing" };

/**
 * Where an asset with a page opens: My content under its type for a content
 * collection, which is the one list of its items, else its own page. Every
 * link to an asset's page goes through here.
 */
export function assetPageDestination(key: AssetKey): AssetDestination {
  return isContentAssetKey(key)
    ? { kind: "content", type: key }
    : { kind: "asset_page", asset: key };
}

export function assetDestination(
  asset: Pick<CoachAssetView, "key" | "target">,
): AssetDestination | null {
  switch (asset.target.kind) {
    case "open_asset":
      return hasAssetPage(assetByKey(asset.key))
        ? assetPageDestination(asset.key)
        : null;
    case "own_page": {
      const opens = assetByKey(asset.key).opens;
      return opens.kind === "page"
        ? { kind: "page", route: opens.route }
        : null;
    }
    case "winback":
    case "winback_trial":
    case "billing":
    case "explain_trial":
      // A suspended subscription is settled on the billing page too; the
      // portal there carries the outstanding invoice.
      return { kind: "billing" };
    case "none":
      return null;
    default: {
      const exhaustive: never = asset.target;
      throw new Error(`Unknown asset target: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/**
 * The site path a destination opens, for a link outside the router, such as
 * an email. Billing opens bare: no screen is waiting to be returned to.
 */
export function assetDestinationPath(destination: AssetDestination): string {
  switch (destination.kind) {
    case "asset_page":
      return `/my-coaching/business/nova/assets/${destination.asset}`;
    case "content":
      return `/my-coaching/business/nova/content?type=${destination.type}`;
    case "page":
      return destination.route;
    case "billing":
      return "/settings/profile/billing";
    default: {
      const exhaustive: never = destination;
      throw new Error(
        `Unknown asset destination: ${JSON.stringify(exhaustive)}`,
      );
    }
  }
}
